import { useRef } from "react";
import { useSubmitAnalyticMutation } from "redux/queries/analytics";

const getInitialData = () => ({
  featureEntry: null,
  cameraOpened: null,
  firstRoiDetected: null,
  successfulResultDetected: null,
  frameSent: null,
  processingTime: null,
  eligibleCount: 0,
  nonEligibleCount: 0,
  errorCount: 0,
  os: null,
  totalTimeSpent: null,
  nationality: null,
  gender: null,
  dateOfBirth: null,
  sent: false,
});

export default function useScanAnalytics(areAnalyticsEnabled = true) {
  const [submitAnalytic] = useSubmitAnalyticMutation();
  const analyticsRef = useRef(getInitialData());

  // If analytics are disabled, return a no-op API
  if (!areAnalyticsEnabled) {
    const noop = () => {};
    return {
      markFeatureEntry: noop,
      markCameraOpened: noop,
      markRoiDetected: noop,
      markSuccessfulResultDetected: noop,
      markFrameSent: noop,
      sendAnalyticsIfNeeded: noop,
      incrementEligible: noop,
      incrementNonEligible: noop,
      incrementError: noop,
      setOS: noop,
      setNationality: noop,
      setGender: noop,
      setDateOfBirth: noop,
      markTotalTimeSpent: noop,
      resetAnalytics: noop,
      analytics: {},
    };
  }

  function update(patch) {
    analyticsRef.current = { ...analyticsRef.current, ...patch };
  }

  function markFeatureEntry() {
    if (!analyticsRef.current.featureEntry) {
      update({ featureEntry: Date.now() });
    }
  }

  function markCameraOpened() {
    if (!analyticsRef.current.cameraOpened) {
      update({ cameraOpened: Date.now() });
    }
  }

  function markFrameSent() {
    update({ frameSent: Date.now() });
  }

  function markRoiDetected() {
    const now = Date.now();
    if (!analyticsRef.current.firstRoiDetected) {
      update({ firstRoiDetected: now });
    }
  }

  function setOS(osString) {
    update({ os: osString });
  }

  function incrementEligible() {
    update({ eligibleCount: (analyticsRef.current.eligibleCount || 0) + 1 });
  }

  function incrementNonEligible() {
    update({
      nonEligibleCount: (analyticsRef.current.nonEligibleCount || 0) + 1,
    });
  }

  function incrementError() {
    update({ errorCount: (analyticsRef.current.errorCount || 0) + 1 });
  }

  function markTotalTimeSpent() {
    if (
      !analyticsRef.current.totalTimeSpent &&
      analyticsRef.current.featureEntry
    ) {
      const now = Date.now();
      update({ totalTimeSpent: now - analyticsRef.current.featureEntry });
    }
  }

  function markSuccessfulResultDetected(extra = {}) {
    if (!analyticsRef.current.successfulResultDetected) {
      const now = Date.now();
      let processingTime = null;
      if (analyticsRef.current.frameSent) {
        processingTime = now - analyticsRef.current.frameSent;
      }
      // Also mark total time spent in feature
      let totalTimeSpent = null;
      if (analyticsRef.current.featureEntry) {
        totalTimeSpent = now - analyticsRef.current.featureEntry;
      }
      update({
        successfulResultDetected: now,
        processingTime,
        totalTimeSpent,
        ...extra,
      });
    }
  }

  function sendAnalyticsIfNeeded(extra = {}) {
    if (analyticsRef.current.sent) return;
    const {
      featureEntry,
      cameraOpened,
      firstRoiDetected,
      successfulResultDetected,
      processingTime,
      eligibleCount,
      nonEligibleCount,
      errorCount,
      os,
      totalTimeSpent,
      nationality,
      gender,
      dateOfBirth,
    } = analyticsRef.current;

    // Accept detail and reason from extra
    const { reason, detail } = extra;
    const dataToSend = {
      type: "scan_timing",
      featureEntry,
      cameraOpened,
      firstRoiDetected,
      successfulResultDetected,
      processingTime,
      eligibleCount,
      nonEligibleCount,
      errorCount,
      os,
      totalTimeSpent,
      nationality,
      gender,
      dateOfBirth,
      reason,
      detail,
      ...extra,
    };
    submitAnalytic(dataToSend);
    update({ sent: true });
  }

  function setNationality(nationality) {
    update({ nationality });
  }

  function setGender(gender) {
    update({ gender });
  }

  function setDateOfBirth(dateOfBirth) {
    update({ dateOfBirth });
  }

  function resetAnalytics() {
    analyticsRef.current = getInitialData();
  }

  return {
    markFeatureEntry,
    markCameraOpened,
    markRoiDetected,
    markSuccessfulResultDetected,
    markFrameSent,
    sendAnalyticsIfNeeded,
    incrementEligible,
    incrementNonEligible,
    incrementError,
    setOS,
    setNationality,
    setGender,
    setDateOfBirth,
    markTotalTimeSpent,
    resetAnalytics,
    analytics: analyticsRef.current,
  };
}
